import {
  HE_DRUG_ADVICE_DOSAGE,
  HE_DRUG_ADVICE_DURATION,
  HE_DRUG_ADVICE_FREQUENCY,
  HE_DRUG_ADVICE_NOTE,
  HE_DRUG_ADVICE_TIMING,
  HE_DRUG_ADVICE_TYPE,
  HE_KEY_PSYCH_ASSESSMENTS,
  IDB_STORES,
} from 'src/consumer/constants'
import { createERMDatabase } from './indexedDb'
import {
  decryptIdbRecord,
  decryptIdbRecords,
  encryptIdbRecord,
  isIdbEncryptionSupported,
} from './idbCrypto'

// All records are encrypted at rest (AES-GCM via idbCrypto) — only the primary key
// stays readable inside IndexedDB. Because the native indexes therefore cannot see
// record fields anymore, every search runs over decrypted records held in an
// in-memory cache (kept up to date by the write helpers), which is also what makes
// per-keystroke searches cheap.

async function getDB() {
  const db = await createERMDatabase()
  return db
}

// indexName (as used by the old index-based search API) -> record field
const INDEX_FIELD_BY_NAME = {}
Object.values(IDB_STORES).forEach((storeConfig) => {
  storeConfig.indexes.forEach(({ name, field }) => {
    INDEX_FIELD_BY_NAME[name] = field
  })
})

// decrypted-records cache: storeName -> Map(id -> record)
const decryptedStoreCache = new Map()

// must be called whenever the underlying database is wiped (e.g. force resync)
export function clearDecryptedStoreCache() {
  decryptedStoreCache.clear()
}

async function getDecryptedStoreMap(storeName) {
  if (decryptedStoreCache.has(storeName)) {
    return decryptedStoreCache.get(storeName)
  }
  const db = await getDB()
  const storedRecords = await db.getAll(storeName)
  const records = await decryptIdbRecords(storedRecords)
  const recordsById = new Map()
  records.forEach((record) => {
    if (record?.id) {
      recordsById.set(record.id, record)
    }
  })
  decryptedStoreCache.set(storeName, recordsById)

  // lazy migration: plaintext leftovers (written by an older app version or in a
  // non-secure dev context) are re-written encrypted in the background — readers
  // already have the data via the cache, so this never blocks anything
  const plaintextLeftovers = storedRecords.filter((record) => record && !record.__enc && record.id)
  if (plaintextLeftovers.length > 0 && isIdbEncryptionSupported()) {
    putRecordsInStore(storeName, plaintextLeftovers).catch((error) =>
      console.warn(
        `Could not re-encrypt ${plaintextLeftovers.length} legacy plaintext records in ${storeName}`,
        error,
      ),
    )
  }
  return recordsById
}

async function getAllRecordsFromStore(storeName) {
  const recordsById = await getDecryptedStoreMap(storeName)
  return Array.from(recordsById.values())
}

async function putRecordsInStore(storeName, records) {
  const validRecords = (records || []).filter((record) => record?.id)
  if (validRecords.length === 0) {
    return
  }
  const encryptedRecords = await Promise.all(
    validRecords.map((record) => encryptIdbRecord(record, 'id')),
  )

  const db = await getDB()
  const tx = db.transaction(storeName, 'readwrite')
  const store = tx.objectStore(storeName)
  for (const encryptedRecord of encryptedRecords) {
    await store.put(encryptedRecord)
  }
  await tx.done

  if (decryptedStoreCache.has(storeName)) {
    const recordsById = decryptedStoreCache.get(storeName)
    validRecords.forEach((record) => recordsById.set(record.id, record))
  }
}

async function getRecordByIdFromStore(storeName, id) {
  if (!id) {
    return null
  }
  if (decryptedStoreCache.has(storeName)) {
    return decryptedStoreCache.get(storeName).get(id) || null
  }
  const db = await getDB()
  const storedRecord = await db.get(storeName, id)
  const record = await decryptIdbRecord(storedRecord)
  return record || null
}

function withTitleIndexField(item) {
  return { ...item, lowerCaseIndexField: item?.title?.toLowerCase() }
}

export function getFunctionForAddOrUpdateHealthEntitySingle(healthEntity, data) {
  return getFunctionForAddOrUpdateHealthEntityMulitple(healthEntity, [data])
}

export function getFunctionForAddOrUpdateHealthEntityMulitple(healthEntity, data) {
  switch (healthEntity) {
    case 'complaints':
      return addOrUpdateHealthComplaintsInIdb(data)
    case 'diagnosis':
      return addOrUpdateDiagnosisInIdb(data)
    case 'observations':
      return addOrUpdateObservationsInIdb(data)
    case 'advices':
      return addOrUpdateAdvicesInIdb(data)
    case 'instructions':
      return addOrUpdateInstructionsInIdb(data)
    case 'investigation':
      return addOrUpdateInvestigationInIdb(data)
    case HE_KEY_PSYCH_ASSESSMENTS:
      return addOrUpdatePsychAssessmentsInIdb(data)
    case HE_DRUG_ADVICE_DOSAGE:
    case HE_DRUG_ADVICE_DURATION:
    case HE_DRUG_ADVICE_FREQUENCY:
    case HE_DRUG_ADVICE_TIMING:
    case HE_DRUG_ADVICE_TYPE:
    case HE_DRUG_ADVICE_NOTE:
      return addOrUpdateDrugAdviceEntitiesInIdb(healthEntity, data)
    default:
      return null
  }
}

// IDB_STORES.HEALTH_COMPLAINTS.name:

export async function addOrUpdateHealthComplaintsInIdb(complaints) {
  await putRecordsInStore(
    IDB_STORES.HEALTH_COMPLAINTS.name,
    (complaints || []).filter((item) => item?.title).map(withTitleIndexField),
  )
}

export async function getAllHealthComplaintsFromIdb() {
  return await getAllRecordsFromStore(IDB_STORES.HEALTH_COMPLAINTS.name)
}

export async function searchHealthComplaintsInIdb(searchTerm) {
  if (!searchTerm) {
    return await getAllHealthComplaintsFromIdb()
  }
  return await searchItemsInIdb(searchTerm, IDB_STORES.HEALTH_COMPLAINTS.name, 'titleIDX', true)
}

export async function getHealthComplaintsByIds(healthComplaintIds) {
  return await getItemsByIds(healthComplaintIds, IDB_STORES.HEALTH_COMPLAINTS.name)
}

// IDB_STORES.DIAGNOSIS.name:

export async function addOrUpdateDiagnosisInIdb(diagnosisList) {
  await putRecordsInStore(
    IDB_STORES.DIAGNOSIS.name,
    (diagnosisList || []).filter((item) => item?.title).map(withTitleIndexField),
  )
}

export async function getAllDiagnosisFromIdb() {
  return await getAllRecordsFromStore(IDB_STORES.DIAGNOSIS.name)
}

export async function searchDiagnosisInIdb(searchTerm) {
  if (!searchTerm) {
    return await getAllDiagnosisFromIdb()
  }
  return await searchItemsInIdb(searchTerm, IDB_STORES.DIAGNOSIS.name, 'titleIDX', true)
}

export async function getDiagnosisByIds(diagnosisIds) {
  return await getItemsByIds(diagnosisIds, IDB_STORES.DIAGNOSIS.name)
}

// IDB_STORES.OBSERVATIONS.name:

export async function addOrUpdateObservationsInIdb(observationsList) {
  await putRecordsInStore(
    IDB_STORES.OBSERVATIONS.name,
    (observationsList || []).filter((item) => item?.title).map(withTitleIndexField),
  )
}

export async function getAllObservationsInIdb() {
  return await getAllRecordsFromStore(IDB_STORES.OBSERVATIONS.name)
}

export async function searchObservationsInIdb(searchTerm) {
  if (!searchTerm) {
    return await getAllObservationsInIdb()
  }
  return await searchItemsInIdb(searchTerm, IDB_STORES.OBSERVATIONS.name, 'titleIDX', true)
}

export async function getObservationsByIds(observationsIds) {
  return await getItemsByIds(observationsIds, IDB_STORES.OBSERVATIONS.name)
}

// IDB_STORES.ADVICE.name:

export async function addOrUpdateAdvicesInIdb(advicesList) {
  await putRecordsInStore(
    IDB_STORES.ADVICE.name,
    (advicesList || []).filter((item) => item?.title).map(withTitleIndexField),
  )
}

export async function getAllAdvicesInIdb() {
  return await getAllRecordsFromStore(IDB_STORES.ADVICE.name)
}

export async function searchAdvicesInIdb(searchTerm) {
  if (!searchTerm) {
    return await getAllAdvicesInIdb()
  }
  return await searchItemsInIdb(searchTerm, IDB_STORES.ADVICE.name, 'titleIDX', true)
}

export async function getAdvicesByIds(advicesByIds) {
  return await getItemsByIds(advicesByIds, IDB_STORES.ADVICE.name)
}

// IDB_STORES.INSTRUCTIONS.name:

export async function addOrUpdateInstructionsInIdb(instructionsList) {
  await putRecordsInStore(
    IDB_STORES.INSTRUCTIONS.name,
    (instructionsList || []).filter((item) => item?.title).map(withTitleIndexField),
  )
}

export async function getAllInstructionsInIdb() {
  return await getAllRecordsFromStore(IDB_STORES.INSTRUCTIONS.name)
}

export async function searchInstructionsInIdb(searchTerm) {
  if (!searchTerm) {
    return await getAllInstructionsInIdb()
  }
  return await searchItemsInIdb(searchTerm, IDB_STORES.INSTRUCTIONS.name, 'titleIDX', true)
}

export async function getInstructionsByIds(instructionsByIds) {
  return await getItemsByIds(instructionsByIds, IDB_STORES.INSTRUCTIONS.name)
}

// IDB_STORES.INVESTIGATION.name:

export async function addOrUpdateInvestigationInIdb(investigationList) {
  await putRecordsInStore(
    IDB_STORES.INVESTIGATION.name,
    (investigationList || []).filter((item) => item?.title).map(withTitleIndexField),
  )
}

export async function getAllInvestigationInIdb() {
  return await getAllRecordsFromStore(IDB_STORES.INVESTIGATION.name)
}

export async function searchInvestigationInIdb(searchTerm) {
  if (!searchTerm) {
    return await getAllInvestigationInIdb()
  }
  return await searchItemsInIdb(searchTerm, IDB_STORES.INVESTIGATION.name, 'titleIDX', true)
}

export async function getInvestigationByIds(investigationByIds) {
  return await getItemsByIds(investigationByIds, IDB_STORES.INVESTIGATION.name)
}

export async function findAllPanelsWithGivenTestId(testId) {
  const allItems = await getAllRecordsFromStore(IDB_STORES.INVESTIGATION.name)
  return allItems.filter(
    (item) => item?.investigationType === 'panel' && item?.testIds?.includes(testId),
  )
}

// IDB_STORES.PSYCH_ASSESSMENTS.name:

export async function addOrUpdatePsychAssessmentsInIdb(psychAssessmentsList) {
  await putRecordsInStore(
    IDB_STORES.PSYCH_ASSESSMENTS.name,
    (psychAssessmentsList || []).filter((item) => item?.title).map(withTitleIndexField),
  )
}

export async function getAllPsychAssessmentsInIdb() {
  return await getAllRecordsFromStore(IDB_STORES.PSYCH_ASSESSMENTS.name)
}

export async function searchPsychAssessmentsInIdb(searchTerm) {
  if (!searchTerm) {
    return await getAllPsychAssessmentsInIdb()
  }
  return await searchItemsInIdb(searchTerm, IDB_STORES.PSYCH_ASSESSMENTS.name, 'titleIDX', true)
}

export async function getPsychAssessmentsByIds(psychAssessmentsByIds) {
  return await getItemsByIds(psychAssessmentsByIds, IDB_STORES.PSYCH_ASSESSMENTS.name)
}

// IDB_STORES.GENERIC_DRUGS.name:

export async function addOrUpdateGenericDrugsInIdb(genericDrugsList) {
  await putRecordsInStore(
    IDB_STORES.GENERIC_DRUGS.name,
    (genericDrugsList || []).filter((item) => item?.title).map(withTitleIndexField),
  )
}

export async function getAllGenericDrugsInIdb() {
  return await getAllRecordsFromStore(IDB_STORES.GENERIC_DRUGS.name)
}

export async function searchGenericDrugsInIdb(searchTerm) {
  if (!searchTerm) {
    return await getAllGenericDrugsInIdb()
  }
  return await searchItemsInIdb(searchTerm, IDB_STORES.GENERIC_DRUGS.name, 'titleIDX', true)
}

export async function findGenericDrugInIdbByMedKey(searchGenericNameId) {
  if (searchGenericNameId == null || searchGenericNameId === '') {
    return null
  }
  const searchKey = Number(searchGenericNameId)
  if (Number.isNaN(searchKey)) {
    return null
  }
  const allGenericDrugs = await getAllRecordsFromStore(IDB_STORES.GENERIC_DRUGS.name)
  return allGenericDrugs.find((item) => Number(item?.medKey) === searchKey) || null
}

export async function getGenericDrugsByIds(genericDrugsByIds) {
  return await getItemsByIds(genericDrugsByIds, IDB_STORES.GENERIC_DRUGS.name)
}

// IDB_STORES.DRUGS.name:

export async function addOrUpdateDrugsInIdb(drugsList) {
  await putRecordsInStore(
    IDB_STORES.DRUGS.name,
    (drugsList || [])
      .filter((drug) => drug?.name)
      .map((drug) => ({ ...drug, lowerCaseIndexField: drug.name.toLowerCase() })),
  )
}

export async function addOrUpdateGenericDrugsInStore(genericDrugNamesList) {
  await putRecordsInStore(
    IDB_STORES.GENERIC_DRUGS.name,
    (genericDrugNamesList || [])
      .filter((drug) => drug?.name)
      .map((drug) => ({ ...drug, lowerCaseIndexField: drug.name.toLowerCase() })),
  )
}

export async function getAllDrugsFromIdb() {
  return await getAllRecordsFromStore(IDB_STORES.DRUGS.name)
}

export async function searchDrugsInIdb(searchTerm, currentMentorId) {
  if (!searchTerm) {
    return await getAllDrugsFromIdb()
  }
  return await searchItemsInIdb(
    searchTerm,
    IDB_STORES.DRUGS.name,
    'titleIDX',
    true,
    currentMentorId,
  )
}

export async function findDrugsByRelatedName(relatedName) {
  if (!relatedName || relatedName.trim() === '') {
    return []
  }
  const allDrugs = await getAllRecordsFromStore(IDB_STORES.DRUGS.name)
  return allDrugs.filter((drug) => drug?.relatedName === relatedName)
}

export async function getDrugsByIDs(drugIds) {
  return await getItemsByIds(drugIds, IDB_STORES.DRUGS.name)
}

//PATIENTS:

export async function addOrUpdatePatientsInIdb(patientsList) {
  await putRecordsInStore(
    IDB_STORES.PATIENTS.name,
    (patientsList || [])
      .filter((patient) => patient?.name)
      .map((patient) => ({
        ...patient,
        ...(patient?.name.toLowerCase() && { nameIDX: patient?.name.toLowerCase() }),
        ...(patient?.mobile && { mobileIDX: patient?.mobile }),
        ...(patient?.email && { emailIDX: patient?.email }),
        ...(patient?.patientNumber && { patientNumberIDX: patient?.patientNumber }),
      })),
  )
}

export async function getAllPatientsFromIdb() {
  return await getAllRecordsFromStore(IDB_STORES.PATIENTS.name)
}

export async function searchPatientsInIdb(searchTerm) {
  if (!searchTerm) {
    const items = await getAllPatientsFromIdb()
    return (items || []).slice(0, 20)
  }
  const nameResults = await searchItemsInIdb(searchTerm, IDB_STORES.PATIENTS.name, 'nameIDX', false)
  const mobileResults = await searchItemsInIdb(
    searchTerm,
    IDB_STORES.PATIENTS.name,
    'mobileIDX',
    false,
  )
  const emailResults = await searchItemsInIdb(
    searchTerm,
    IDB_STORES.PATIENTS.name,
    'emailIDX',
    false,
  )
  const patientNumberResults = await searchItemsInIdb(
    searchTerm,
    IDB_STORES.PATIENTS.name,
    'patientNumberIDX',
    false,
  )

  const allResults = [...nameResults, ...mobileResults, ...emailResults, ...patientNumberResults]

  const uniqueResults = Array.from(new Set(allResults.map((patient) => patient.id))).map((id) =>
    allResults.find((patient) => patient.id === id),
  )

  return uniqueResults
}

export async function getPatientsByIds(patientIds) {
  return await getItemsByIds(patientIds, IDB_STORES.PATIENTS.name)
}

// CLINICAL ENTITIES (clinical-diagnosis / clinical-symptom / clinical-descriptor):
// ICD-10-aware stores. Search matches title (contains), altTitles (contains) and
// code/icd10Code (prefix, so typing "F32" works), mirroring the server's behaviour;
// specializationId / leafOnly filters replicate HealthEntityDao semantics (missing
// specializationIds = unclassified = always visible; leafOnly excludes only leaf:false).

const CLINICAL_ENTITY_STORE_BY_PARAM = {
  'clinical-diagnosis': IDB_STORES.CLINICAL_DIAGNOSIS.name,
  'clinical-symptom': IDB_STORES.CLINICAL_SYMPTOMS.name,
  'clinical-descriptor': IDB_STORES.CLINICAL_DESCRIPTORS.name,
}

export const CLINICAL_ENTITY_PARAMS_WITH_IDB_STORE = Object.keys(CLINICAL_ENTITY_STORE_BY_PARAM)

export async function addOrUpdateClinicalEntitiesInIdb(entityParam, items) {
  const storeName = CLINICAL_ENTITY_STORE_BY_PARAM[entityParam]
  if (!storeName) {
    return
  }
  await putRecordsInStore(
    storeName,
    (items || []).filter((item) => item?.title).map(withTitleIndexField),
  )
}

export async function clinicalEntityStoreHasData(entityParam) {
  const storeName = CLINICAL_ENTITY_STORE_BY_PARAM[entityParam]
  if (!storeName) {
    return false
  }
  const records = await getAllRecordsFromStore(storeName)
  return records.length > 0
}

export async function getClinicalEntitiesByIds(entityParam, ids) {
  const storeName = CLINICAL_ENTITY_STORE_BY_PARAM[entityParam]
  if (!storeName) {
    return []
  }
  return await getItemsByIds(ids, storeName)
}

function clinicalEntityMatchesSearch(record, searchLowercase) {
  if (!searchLowercase) {
    return true
  }
  if (record?.title?.toLowerCase()?.includes(searchLowercase)) {
    return true
  }
  if ((record?.altTitles || []).some((alt) => String(alt).toLowerCase().includes(searchLowercase))) {
    return true
  }
  const code = record?.icd10Code || record?.code
  return Boolean(code && String(code).toLowerCase().startsWith(searchLowercase))
}

export async function searchClinicalEntitiesInIdb(
  entityParam,
  searchTerm,
  { disciplineSlugs, leafOnly } = {},
) {
  const storeName = CLINICAL_ENTITY_STORE_BY_PARAM[entityParam]
  if (!storeName) {
    return []
  }
  const searchLowercase = (searchTerm || '').trim().toLowerCase()
  const allRecords = await getAllRecordsFromStore(storeName)
  const disciplines = (disciplineSlugs || []).filter(Boolean)

  const results = allRecords.filter((record) => {
    if (record?.enabled === false || record?.deleted === true) {
      return false
    }
    if (leafOnly && record?.icdClassification?.leaf === false) {
      return false
    }
    if (disciplines.length > 0) {
      const tagged = record?.specializationIds
      if (Array.isArray(tagged) && tagged.length > 0 && !tagged.some((s) => disciplines.includes(s))) {
        return false
      }
    }
    return clinicalEntityMatchesSearch(record, searchLowercase)
  })

  // code-prefix hits first (typing "F32" should surface F32 before "...F32..." titles),
  // then title-prefix, then the rest; alphabetical inside each band
  const rank = (record) => {
    const code = String(record?.icd10Code || record?.code || '').toLowerCase()
    if (searchLowercase && code.startsWith(searchLowercase)) return 0
    if (searchLowercase && record?.title?.toLowerCase()?.startsWith(searchLowercase)) return 1
    return 2
  }
  return results.sort((a, b) => {
    const rankDiff = rank(a) - rank(b)
    if (rankDiff !== 0) return rankDiff
    const codeDiff = String(a?.icd10Code || a?.code || '').localeCompare(
      String(b?.icd10Code || b?.code || ''),
    )
    if (codeDiff !== 0) return codeDiff
    return String(a?.title || '').localeCompare(String(b?.title || ''))
  })
}

// DRUG ADVICE ENTITIES (dosage, duration, frequency, timing, type, note):

const DRUG_ADVICE_ENTITY_STORE_BY_PARAM = {
  [HE_DRUG_ADVICE_DOSAGE]: IDB_STORES.DRUG_ADVICE_DOSAGE.name,
  [HE_DRUG_ADVICE_DURATION]: IDB_STORES.DRUG_ADVICE_DURATION.name,
  [HE_DRUG_ADVICE_FREQUENCY]: IDB_STORES.DRUG_ADVICE_FREQUENCY.name,
  [HE_DRUG_ADVICE_TIMING]: IDB_STORES.DRUG_ADVICE_TIMING.name,
  [HE_DRUG_ADVICE_TYPE]: IDB_STORES.DRUG_ADVICE_TYPE.name,
  [HE_DRUG_ADVICE_NOTE]: IDB_STORES.DRUG_ADVICE_NOTE.name,
}

export const DRUG_ADVICE_ENTITY_PARAMS_WITH_IDB_STORE = Object.keys(
  DRUG_ADVICE_ENTITY_STORE_BY_PARAM,
)

export async function addOrUpdateDrugAdviceEntitiesInIdb(entityParam, items) {
  const storeName = DRUG_ADVICE_ENTITY_STORE_BY_PARAM[entityParam]
  if (!storeName) {
    return
  }
  await putRecordsInStore(
    storeName,
    (items || [])
      .filter((item) => item?.title || item?.shortTitle)
      .map((item) => ({
        ...item,
        lowerCaseIndexField: (item?.title || item?.shortTitle)?.toLowerCase(),
      })),
  )
}

export async function getAllDrugAdviceEntitiesFromIdb(entityParam) {
  const storeName = DRUG_ADVICE_ENTITY_STORE_BY_PARAM[entityParam]
  if (!storeName) {
    return []
  }
  return await getAllRecordsFromStore(storeName)
}

export async function searchDrugAdviceEntitiesInIdb(entityParam, searchTerm) {
  const storeName = DRUG_ADVICE_ENTITY_STORE_BY_PARAM[entityParam]
  if (!storeName) {
    return []
  }
  if (!searchTerm) {
    return await getAllRecordsFromStore(storeName)
  }
  return await searchItemsInIdb(searchTerm, storeName, 'titleIDX', true)
}

//
//
//

export async function searchItemsInIdb(
  searchTerm,
  storeName,
  indexName,
  onlyStartsWithSearch = true,
  currentMentorId,
) {
  if (!searchTerm || searchTerm.trim() === '') {
    return []
  }
  const fieldName = INDEX_FIELD_BY_NAME[indexName] || indexName
  const searchLowercase = searchTerm.toLowerCase()

  const allRecords = await getAllRecordsFromStore(storeName)
  // always CONTAINS-match (the server list endpoints match contains too — prefix-only
  // hid valid entries); onlyStartsWithSearch now just controls prefix-first ranking
  let searchResults = allRecords.filter((record) => {
    const fieldValue = record?.[fieldName]
    if (!fieldValue) {
      return false
    }
    return String(fieldValue).toLowerCase().includes(searchLowercase)
  })

  if (storeName === IDB_STORES.DRUGS.name) {
    searchResults = searchResults.sort((a, b) => {
      // Helper function to get priority for sorting
      const getPriority = (item) => {
        // Priority 1: User's own content
        if (item.doctorId === currentMentorId) return 1
        // Priority 2: Content with no doctorId or availableToAll
        if (!item.doctorId || item.doctorId === '' || item.availableToAll === true) return 2
        // Priority 3: Other doctors' content
        return 3
      }

      const priorityA = getPriority(a)
      const priorityB = getPriority(b)

      // Primary Sort: By priority (lower number = higher priority)
      if (priorityA !== priorityB) {
        return priorityA - priorityB
      }

      // Secondary Sort: Entries with a genericNameId come first
      if (a.genericNameId && !b.genericNameId) return -1
      if (!a.genericNameId && b.genericNameId) return 1

      // Tertiary Sort: Alphabetically by title (A-Z)
      return (a.title || '').localeCompare(b.title || '')
    })
  } else if (onlyStartsWithSearch) {
    // prefix matches first (the old prefix-only behaviour, as a ranking), then alphabetical
    const startsWith = (record) =>
      String(record?.[fieldName] || '').toLowerCase().startsWith(searchLowercase) ? 0 : 1
    searchResults = searchResults.sort(
      (a, b) =>
        startsWith(a) - startsWith(b) ||
        String(a?.[fieldName] || '').localeCompare(String(b?.[fieldName] || '')),
    )
  }

  return searchResults
}

export async function searchItemsInIdbById(id, storeName) {
  return await getRecordByIdFromStore(storeName, id)
}

export async function getItemsByIds(ids, storeName) {
  if (!Array.isArray(ids) || ids.length === 0) {
    return []
  }
  const results = await Promise.all(ids.map((id) => getRecordByIdFromStore(storeName, id)))
  return results.filter((item) => item !== null)
}
